import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Col, Row, Skeleton, Steps, theme } from "antd";
import { StickyRightColumn } from "./layout/StickyRightColumn.js";
import { __ } from "../../../utils/i18n.js";
/**
 * Loading placeholder — same class set as the live checkout (hero, stepper,
 * 2-col card) so the skeleton→content swap is zero-shift, including on
 * mobile. Gated by `useDelayedReveal` so fast loads skip it.
 *
 * Keep this in sync with layout changes in `TwoColumnLayout` / `HeroSection` /
 * `ProgressIndicator` / `OrderSummary` / `IdentificationForm` — a mismatch
 * causes a visible jump on swap that's easy to miss on dev networks.
 */ const CheckoutSkeleton = ()=>{
    const { token } = theme.useToken();
    const fieldRow = ()=>/*#__PURE__*/ _jsxs("div", {
            className: "checkout-v2-skeleton-field",
            children: [
                /*#__PURE__*/ _jsx(Skeleton.Input, {
                    active: true,
                    size: "small",
                    style: {
                        width: 90,
                        height: 14,
                        marginBottom: 6
                    }
                }),
                /*#__PURE__*/ _jsx(Skeleton.Input, {
                    active: true,
                    block: true,
                    style: {
                        height: 36
                    }
                })
            ]
        });
    return /*#__PURE__*/ _jsxs("div", {
        "aria-busy": "true",
        "aria-live": "polite",
        "aria-label": __("Loading checkout"),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-hero",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-hero__logo",
                        children: /*#__PURE__*/ _jsx(Skeleton.Input, {
                            active: true,
                            size: "large",
                            className: "checkout-v2-hero__logo-skeleton"
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-hero__headline",
                        children: /*#__PURE__*/ _jsx(Skeleton.Input, {
                            active: true,
                            style: {
                                width: 520,
                                maxWidth: "100%",
                                height: 22
                            }
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-sticky-stepper",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        "aria-hidden": true,
                        className: "checkout-v2-sticky-stepper__backdrop"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "checkout-v2-sticky-stepper__content",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "checkout-v2-stepper-scroll",
                                children: /*#__PURE__*/ _jsx(Steps, {
                                    responsive: false,
                                    size: "small",
                                    current: 1,
                                    items: [
                                        {
                                            title: __("Plan selected"),
                                            status: "finish"
                                        },
                                        {
                                            title: __("Your details"),
                                            status: "process"
                                        },
                                        {
                                            title: __("Payment"),
                                            status: "wait"
                                        },
                                        // Last-step label depends on productId (License & Download vs Order
                                        // confirmed) which we don't have yet — shimmer instead of guessing.
                                        {
                                            title: /*#__PURE__*/ _jsx(Skeleton.Input, {
                                                active: true,
                                                size: "small",
                                                style: {
                                                    width: 120,
                                                    height: 14
                                                }
                                            }),
                                            status: "wait"
                                        }
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "checkout-v2-stepper-segments",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "checkout-v2-stepper-segments__header",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "checkout-v2-stepper-segments__label",
                                                children: __("Your details")
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "checkout-v2-stepper-segments__counter",
                                                children: __("Step %d of %d", 2, 4)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "checkout-v2-stepper-segments__bar",
                                        "aria-hidden": true,
                                        children: [
                                            0,
                                            1,
                                            2,
                                            3
                                        ].map((index)=>/*#__PURE__*/ _jsx("span", {
                                                className: "checkout-v2-stepper-segments__segment",
                                                style: {
                                                    background: index <= 1 ? token.colorPrimary : token.colorFillTertiary
                                                }
                                            }, index))
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "checkout-v2-card",
                style: {
                    background: token.colorBgContainer
                },
                children: /*#__PURE__*/ _jsxs(Row, {
                    gutter: 0,
                    wrap: true,
                    children: [
                        /*#__PURE__*/ _jsxs(Col, {
                            xs: {
                                span: 24,
                                order: 2
                            },
                            lg: {
                                span: 16,
                                order: 1
                            },
                            className: "checkout-v2-card__col--main",
                            children: [
                                /*#__PURE__*/ _jsx(Skeleton.Input, {
                                    active: true,
                                    size: "large",
                                    style: {
                                        width: 140,
                                        height: 22,
                                        marginBottom: 20
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Skeleton.Button, {
                                    active: true,
                                    size: "small",
                                    block: true,
                                    style: {
                                        marginBottom: 20,
                                        height: 36
                                    }
                                }),
                                fieldRow(),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "checkout-v2-skeleton-field-row",
                                    children: [
                                        fieldRow(),
                                        fieldRow()
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Skeleton.Input, {
                                    active: true,
                                    size: "small",
                                    style: {
                                        width: 220,
                                        height: 20,
                                        margin: "4px 0 16px"
                                    }
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "checkout-v2-skeleton-field-row",
                                    children: [
                                        fieldRow(),
                                        fieldRow()
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Skeleton.Input, {
                                    active: true,
                                    size: "small",
                                    style: {
                                        width: "80%",
                                        height: 16,
                                        marginBottom: 8
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Skeleton.Input, {
                                    active: true,
                                    size: "small",
                                    style: {
                                        width: "70%",
                                        height: 16,
                                        marginBottom: 24
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Skeleton.Button, {
                                    active: true,
                                    block: true,
                                    size: "large",
                                    style: {
                                        height: 52,
                                        borderRadius: 4,
                                        marginBottom: 16
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Skeleton.Input, {
                                    active: true,
                                    block: true,
                                    size: "small",
                                    style: {
                                        height: 40,
                                        borderRadius: 4,
                                        background: token.colorFillQuaternary
                                    }
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Col, {
                            xs: {
                                span: 24,
                                order: 1
                            },
                            lg: {
                                span: 8,
                                order: 2
                            },
                            className: "checkout-v2-card__col--summary",
                            style: {
                                background: token.colorBgLayout,
                                "--rc-checkout-v2-border-color": token.colorBorder
                            },
                            children: /*#__PURE__*/ _jsxs(StickyRightColumn, {
                                children: [
                                    /*#__PURE__*/ _jsx(Skeleton.Input, {
                                        active: true,
                                        style: {
                                            width: 120,
                                            height: 22,
                                            marginBottom: 16
                                        }
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "checkout-v2-skeleton-summary-row",
                                        children: [
                                            /*#__PURE__*/ _jsx(Skeleton.Input, {
                                                active: true,
                                                size: "small",
                                                style: {
                                                    width: 140,
                                                    height: 16
                                                }
                                            }),
                                            /*#__PURE__*/ _jsx(Skeleton.Input, {
                                                active: true,
                                                size: "small",
                                                style: {
                                                    width: 90,
                                                    height: 16
                                                }
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Skeleton.Input, {
                                        active: true,
                                        size: "small",
                                        style: {
                                            width: 80,
                                            height: 14,
                                            margin: "14px 0 8px"
                                        }
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "checkout-v2-skeleton-summary-row",
                                        children: [
                                            /*#__PURE__*/ _jsx(Skeleton.Input, {
                                                active: true,
                                                size: "small",
                                                style: {
                                                    width: 60,
                                                    height: 20
                                                }
                                            }),
                                            /*#__PURE__*/ _jsx(Skeleton.Input, {
                                                active: true,
                                                size: "small",
                                                style: {
                                                    width: 90,
                                                    height: 20
                                                }
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "checkout-v2-skeleton-moneyback",
                                        style: {
                                            borderColor: "#b7eb8f",
                                            marginTop: 24
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(Skeleton.Avatar, {
                                                active: true,
                                                size: "small",
                                                shape: "circle"
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                style: {
                                                    flex: 1
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx(Skeleton.Input, {
                                                        active: true,
                                                        size: "small",
                                                        style: {
                                                            width: 160,
                                                            height: 16,
                                                            marginBottom: 6
                                                        }
                                                    }),
                                                    /*#__PURE__*/ _jsx(Skeleton.Input, {
                                                        active: true,
                                                        size: "small",
                                                        style: {
                                                            width: "100%",
                                                            height: 14
                                                        }
                                                    }),
                                                    /*#__PURE__*/ _jsx(Skeleton.Input, {
                                                        active: true,
                                                        size: "small",
                                                        style: {
                                                            width: "80%",
                                                            height: 14,
                                                            marginTop: 4
                                                        }
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "checkout-v2-trust-in-summary checkout-v2-skeleton-benefits",
                                        style: {
                                            marginTop: 24
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(Skeleton.Input, {
                                                active: true,
                                                size: "small",
                                                style: {
                                                    width: 110,
                                                    height: 16,
                                                    marginBottom: 12
                                                }
                                            }),
                                            [
                                                0,
                                                1,
                                                2
                                            ].map((i)=>/*#__PURE__*/ _jsxs("div", {
                                                    className: "checkout-v2-skeleton-benefits__item",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Skeleton.Avatar, {
                                                            active: true,
                                                            size: "small",
                                                            shape: "circle"
                                                        }),
                                                        /*#__PURE__*/ _jsx(Skeleton.Input, {
                                                            active: true,
                                                            size: "small",
                                                            style: {
                                                                width: "85%",
                                                                height: 14
                                                            }
                                                        })
                                                    ]
                                                }, i))
                                        ]
                                    })
                                ]
                            })
                        })
                    ]
                })
            })
        ]
    });
};
export { CheckoutSkeleton };
